import { ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { logEvent } from "@/lib/analytics";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["S","M","T","W","T","F","S"];

const THEMES = {
  dark: {
    container: "bg-[#0d1730] border-emerald-500/20 shadow-2xl shadow-emerald-950/40",
    title: "text-emerald-300",
    navBtn: "bg-white/5 hover:bg-emerald-500/20 text-emerald-200",
    label: "text-emerald-100",
    dow: "text-slate-500",
    cell: "border-white/5 bg-white/[0.03] hover:border-emerald-500/30",
    cellToday: "border-emerald-400 bg-emerald-500/10",
    dayNum: "text-slate-400",
    dayNumToday: "text-emerald-300 font-bold",
    legend: "text-slate-400 border-white/5",
    sel: "ring-2 ring-[#34d399]",
    dots: { personal: "bg-sky-400", business: "bg-[#FFC43D]", bill: "bg-rose-500", budget: "bg-amber-400" },
  },
  light: {
    container: "bg-white border-[var(--line)] shadow-sm",
    title: "text-[var(--text)]",
    navBtn: "bg-[#faf9f6] hover:bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--line)]",
    label: "text-[var(--text)]",
    dow: "text-[var(--text-2)]",
    cell: "border-[var(--line)] bg-[#faf9f6] hover:border-[var(--accent)]/40",
    cellToday: "border-[var(--accent)] bg-[var(--accent)]/10",
    dayNum: "text-[var(--text-2)]",
    dayNumToday: "text-[var(--accent)] font-bold",
    legend: "text-[var(--text-2)] border-[var(--line)]",
    sel: "ring-2 ring-[var(--accent)]",
    dots: { personal: "bg-sky-500", business: "bg-[#FFC43D]", bill: "bg-[#dc2626]", budget: "bg-[#b7791f]" },
  },
  pulse: {
    container: "bg-[#0D1B2A] border-[#2EC4B6]/25 shadow-lg shadow-[#0D1B2A]/40",
    title: "text-[#2EC4B6]",
    navBtn: "bg-white/5 hover:bg-[#1B998B]/30 text-[#2EC4B6]",
    label: "text-white",
    dow: "text-white/50",
    cell: "border-white/8 bg-white/[0.03] hover:border-[#2EC4B6]/40",
    cellToday: "border-[#1B998B] bg-[#1B998B]/20",
    dayNum: "text-white/70",
    dayNumToday: "text-[#2EC4B6] font-bold",
    legend: "text-white/60 border-white/10",
    sel: "ring-2 ring-[#2EC4B6]",
    dots: { personal: "bg-sky-400", business: "bg-[#FFC43D]", bill: "bg-[#FF6B6B]", budget: "bg-[#FF6B6B]" },
  },
};

export default function UnifiedCalendar({ data, month, year, onPrev, onNext, theme = "dark", title = "Pulse", testidPrefix = "snap-cal", className = "w-full lg:w-2/5", onSelectDay, selectedDay }) {
  const t = THEMES[theme] || THEMES.dark;
  const days = data?.days_in_month || 30;
  const first = data?.first_weekday ?? 0;
  const events = data?.events || {};
  const today = new Date();
  const isThisMonth = today.getMonth() + 1 === month && today.getFullYear() === year;

  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div data-onboard="calendar" className={`${className} border rounded-2xl p-5 sm:p-6 ${t.container}`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className={`text-lg font-bold tracking-wide flex items-center gap-2 ${t.title}`}>
          <Zap className={`w-5 h-5 ${theme === "light" ? "text-[var(--accent)]" : ""}`} /> {title}
        </h2>
        <div className="flex items-center gap-1 text-sm">
          <button data-testid={`${testidPrefix}-prev`} onClick={onPrev} className={`w-8 h-8 grid place-items-center rounded-lg transition-colors ${t.navBtn}`}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid={`${testidPrefix}-label`} className={`px-2 min-w-[120px] text-center font-medium ${t.label}`}>{MONTHS[month - 1]} {year}</span>
          <button data-testid={`${testidPrefix}-next`} onClick={onNext} className={`w-8 h-8 grid place-items-center rounded-lg transition-colors ${t.navBtn}`}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-7 gap-1.5 text-center text-[11px] font-semibold mb-2 ${t.dow}`}>
        {DOW.map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const evs = events[String(d)] || [];
          const isToday = isThisMonth && d === today.getDate();
          return (
            <div
              key={i}
              data-testid={`${testidPrefix}-day-${d}`}
              title={evs.map((e) => e.label).join(", ")}
              onClick={() => {
                if (evs.length) logEvent("calendar_event_click", { day: d, count: evs.length, kinds: evs.map((e) => e.kind) });
                if (onSelectDay) onSelectDay(d);
              }}
              className={`aspect-square rounded-lg p-1 flex flex-col justify-between border transition-colors ${onSelectDay ? "cursor-pointer" : "cursor-default"} ${selectedDay === d ? t.sel : ""} ${isToday ? t.cellToday : t.cell}`}
            >
              <span className={`font-mono text-[11px] ${isToday ? t.dayNumToday : t.dayNum}`}>{d}</span>
              <div className="flex flex-wrap gap-0.5 justify-center pb-0.5">
                {evs.slice(0, 4).map((e, j) => (
                  <span key={j} className={`h-1.5 w-1.5 rounded-full ${t.dots[e.kind] || "bg-slate-400"} ${(e.kind === "bill" || e.kind === "budget") ? "animate-pulse" : ""}`} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className={`mt-5 pt-4 border-t grid grid-cols-2 gap-2 text-[11px] ${t.legend}`}>
        {(() => {
          const items = [["personal", "Personal"], ["business", "Business"]];
          if (t.dots.bill === t.dots.budget) items.push(["bill", "Due / Alerts"]);
          else { items.push(["bill", "Bill due"]); items.push(["budget", "Budget due"]); }
          return items.map(([k, l]) => (
            <div key={k} className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${t.dots[k]}`} /> {l}
            </div>
          ));
        })()}
      </div>
    </div>
  );
}
